/*
 *   File : dob-history.js
 *   Author : https://evoqins.com
 *   Description : Dob change history page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// Custom components 
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSearchInput } from '../../../Component/FormElements';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { Icon } from '../../../Component/Icon';
import { DownloadButton } from '../../../Component/Buttons';
import { toast } from 'react-toastify';

const UPDATE_REQUEST_SORT_FIELDS = [
    'customer_name',
    'customer_email',
    'customer_mobile',
    'type',
    'status',
    'approved_by',
    'approved_on',
    'created',
];

const DobChangeHistory = () => {

    const navigate = useNavigate();
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [searchValue, setSearchValue] = useState("");
    const [historyData, setHistoryData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);


    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getHistoryData();
    }, [searchValue, currentPage]);

    // Dob change history table columns
    const TABLE_COLUMNS = [
        {
            name: 'Name',
            id: 'customer_name',
            sortField: 'customer_name',
            selector: row => row.customer_name,
            width: "280px",
            sortable: true
        },
        // {
        //     name: 'Email',
        //     // selector: row => row.customer_email !== null ? row.customer_email : '-',
        //     cell: row =>
        //         <div>
        //             {row.customer_email !== null ?
        //                 <a href={`mailto:${row.customer_email}`} className='text-decoration-none text-decoration-hover-underline e-text-rich-black' >
        //                     {row.customer_email}
        //                 </a>
        //                 :
        //                 '-'
        //             }
        //         </div>,
        //     width: "280px"
        // },
        // {
        //     name: 'Phone no.',
        //     cell: row =>
        //         <a href={`tel:${row.customer_mobile}`} className='text-decoration-none text-decoration-hover-underline e-text-rich-black' >
        //             {row.customer_mobile}
        //         </a>,
        //     width: "160px"
        // },
        {
            name: 'Request type',
            id: 'type',
            sortField: 'type',
            cell: (row) => <>{`${row.type_text} change` || "-"}</>,
            width: "140px",
            sortable: true
        },
        {
            name: 'Old value',
            cell: (row) =>
                <p className='mb-0'>
                    {format(row.data.old_data, 'PP')}
                </p>,
            width: "140px"
        },
        {
            name: 'New value',
            cell: (row) =>
                <p className='mb-0'>
                    {format(row.data.new_data, 'PP')}
                </p>,
            width: "140px"
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            cell: row =>
                <div className={`${row.is_approved ? `e-bg-tea-green padding-12px-lr` : `e-bg-light-red padding-12px-lr`} py-1 e-border-radius-16 e-font-weight-500 e-font-12`}>
                    {row.is_approved ? "Approved" : "Rejected"}
                </div>,
            width: "120px",
            sortable: true
        },
        {
            name: 'Country admin',
            id: 'approved_by',
            sortField: 'approved_by',
            selector: row => row.approved_by,
            width: "180px",
            sortable: true
        },
        {
            name: 'Status updated on',
            id: 'approved_on',
            sortField: 'approved_on',
            selector: row => row.approved_on,
            width: "180px",
            sortable: true
        },
    ];

    // handle page change 
    const _handlePageChange = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !UPDATE_REQUEST_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getHistoryData(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // handle clear searched value 
    const _handleClearSearchValue = () => {
        setSearchValue("");
        setLoading(true);
        setSortField(null);
        setSortDirection(null);
    };

    // handle back to list 
    const _handleBack = () => {
        navigate("/manage-request", {
            state: 2
        });
    };

    // API - get registration history 
    const _getHistoryData = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        let is_post = true;
        let url = 'CA/update-request/list';
        let data = {
            "page_num": page_num,
            "query": searchValue,
            "filter": 1,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null,
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setHistoryData(response.data)
            } else {
            }
            setLoading(false);
        });
    };

    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "CA/update-request/download";
        let data = {
            "filter": 1,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='d-flex gap-0-8 align-items-center'>
                    <img src={require("../../../Assets/Image/left-back-arrow.svg").default}
                        draggable={false}
                        className='cursor-pointer'
                        alt='arrow'
                        onClick={_handleBack} />
                    <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Change requests history
                    </h1>
                </div>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                    <div className='row px-4'>
                        <div className='col-6'>
                            <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px' >
                                Requests
                            </h4>
                        </div>
                        <div className='col-6 text-end'>
                            <div className='d-flex align-items-center gap-4 justify-content-end'>

                                <DownloadButton loader={downloadLoader}
                                    onClick={_download} />

                                {/* search input */}
                                <div className='position-relative'>
                                    <CustomSearchInput placeholder="Search by name, id & store"
                                        type="text"
                                        require
                                        value={searchValue}
                                        className="w-298px  padding-10px-tb"
                                        labelStyle="e-font-weight-500"
                                        handleChange={(e) => {
                                            setSearchValue(e);
                                            setCurrentPage(1);
                                        }}
                                        handleClear={() => _handleClearSearchValue()} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading ?
                        // loader 
                        <Loader />
                        :
                        <>
                            {/* request history data table */}
                            {historyData.data.length !== 0 ?
                                <div className='e-table'>
                                    <DataTableComponent
                                        key={`dob-history-table-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        data={historyData.data}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationTotalRows={historyData.total_records}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange} />
                                </div>
                                :
                                <div className='h-100vh pt-5 mt-5'>
                                    <EmptyScreen message="No requests found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        </section>
    )
}

export default DobChangeHistory;
